import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import {
  ConsultationPaymentReceipt,
  MembershipDependent,
  PaymentMethod,
} from "@/services/core-api-adapter";
import theme, { defaultFullscreenPageStyling, panelStyle } from "@/theme";
import {
  convertPxToRem,
  formatAmount,
  formatDateToReadableString,
  getShortDayFromDate,
} from "@/utils";
import { Box, Divider, Link, Stack, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import InfoContextPanel from "@/features/Common/components/InfoContextPanel";
import { useNavigate } from "react-router";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";

enum PaymentStatus {
  AUTHORIZE = "Authorize",
  PAYMENT = "Payment",
  CAPTURE = "Capture",
  REFUND = "Refund",
  VOID = "Void",
}

interface ConsultationOrderSummaryProps {
  consultationPaymentReceipt: ConsultationPaymentReceipt | null;
  consultationId: string;
  selectedDependant?: MembershipDependent | null;
  onViewReceiptClick: () => void;
  onBackButtonClick: () => void;
  onRateServiceClick?: () => void;
}

export default function ConsultationOrderSummary({
  consultationPaymentReceipt,
  consultationId,
  selectedDependant,
  onViewReceiptClick,
  onBackButtonClick,
  onRateServiceClick,
}: ConsultationOrderSummaryProps) {
  const { t } = useTranslation();
  const [consultationType, setConsultationType] = useState<string>("");
  const [isCareCardVoucherPurchase, setIsCareCardVoucherPurchase] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const isDiscountVouchersEnabled = isFeatureEnabled(
    FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
  );

  useEffect(() => {
    if (
      consultationPaymentReceipt?.chargeCode === "ONLINE_DOCTOR_CONSULTATION"
    ) {
      setConsultationType(t("ConsultationOrderSummary.doctorNurse.title"));
    } else if (
      consultationPaymentReceipt?.chargeCode === "DIGITAL_DOCTOR_CONSULT"
    ) {
      setConsultationType(t("ConsultationOrderSummary.doctor.title"));
    } else setConsultationType("");

    setIsCareCardVoucherPurchase(
      consultationPaymentReceipt?.paymentMethod ===
        PaymentMethod.CareCardVoucher
    );
  }, [
    consultationPaymentReceipt?.chargeCode,
    t,
    consultationPaymentReceipt?.paymentMethod,
  ]);

  function getConsultationOrderPaymentStatus() {
    if (
      !consultationPaymentReceipt?.status ||
      !Object.values(PaymentStatus).includes(
        consultationPaymentReceipt?.status as PaymentStatus
      )
    )
      return null;

    const isPaid =
      consultationPaymentReceipt?.status === PaymentStatus.PAYMENT ||
      consultationPaymentReceipt?.status === PaymentStatus.CAPTURE ||
      consultationPaymentReceipt?.status === PaymentStatus.AUTHORIZE;

    return (
      <>
        <IconLoader
          icon="CheckInCircleIcon"
          sx={{
            color: theme.palette.accent1.main,
            fontSize: convertPxToRem(16),
          }}
        />
        <Typography
          fontSize={convertPxToRem(10)}
          fontWeight="600"
          color="accent1.main"
        >
          {isPaid
            ? t("ConsultationOrderSummary.status.paid")
            : t("ConsultationOrderSummary.status.refunded")}
        </Typography>
      </>
    );
  }

  function getOrderSummaryTotalAmount() {
    if (isCareCardVoucherPurchase) {
      return consultationPaymentReceipt?.voucherCode;
    } else if (
      isDiscountVouchersEnabled &&
      consultationPaymentReceipt?.discountVoucherDetails
    ) {
      return formatAmount(
        consultationPaymentReceipt?.originalAmount || 0,
        consultationPaymentReceipt?.currencyCode || "ZAR"
      );
    } else {
      return formatAmount(
        consultationPaymentReceipt?.paymentAmount || 0,
        consultationPaymentReceipt?.currencyCode || "ZAR"
      );
    }
  }

  return (
    <Stack sx={{ ...defaultFullscreenPageStyling }}>
      <CircledBackButton showLabel={true} onClick={onBackButtonClick} />
      <Stack justifyContent="center" alignItems="center" pt={5}>
        <Typography variant="h2" component="h1">
          {t("OrderSummary.title")}
        </Typography>
        <Typography variant="h4" pt={1}>
          {t("ConsultationOrderSummary.consultationNumber.title")}
        </Typography>
        <Typography variant="body2" pb={1}>
          {consultationId}
        </Typography>

        {!!selectedDependant && (
          <Stack alignItems="center">
            <InfoContextPanel
              title={t("SelectedDependantPanel.title")}
              text={
                selectedDependant.firstName + " " + selectedDependant.lastName
              }
              compact
              compactText={
                selectedDependant.firstName + " " + selectedDependant.lastName
              }
            />
          </Stack>
        )}

        <Stack
          direction="row"
          gap={1}
          pt={1}
          alignItems="center"
          justifyContent="center"
        >
          {!getConsultationOrderPaymentStatus() ? null : (
            <Stack
              aria-label={t("ConsultationOrderSummary.status.ariaLabel")}
              display="inline-flex"
              direction="row"
              alignItems="center"
              alignContent="center"
              alignSelf="flex-start"
              borderRadius={1}
              spacing={0.25}
              p={0.5}
              bgcolor="transparent"
              sx={{
                border: `2px solid ${theme.palette.accent1.main}`,
                height: "auto",
                width: "auto",
              }}
            >
              {getConsultationOrderPaymentStatus()}
            </Stack>
          )}

          <Typography variant="body2">
            {consultationPaymentReceipt?.paymentUtcDateTime
              ? getShortDayFromDate(
                  new Date(consultationPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
            ,{" "}
            {consultationPaymentReceipt?.paymentUtcDateTime
              ? formatDateToReadableString(
                  new Date(consultationPaymentReceipt.paymentUtcDateTime)
                )
              : ""}
          </Typography>
        </Stack>

        <Stack
          spacing={2}
          padding={2}
          pt={5}
          width="100%"
          sx={{ ...panelStyle, marginTop: 2 }}
          aria-label="consultation-order-summary-panel"
        >
          <Typography
            variant="h4"
            fontWeight={600}
            aria-label={consultationType}
          >
            {consultationType}
          </Typography>
          <Typography variant="body2">
            {t("ConsultationOrderSummary.panel.firstDescription")}
            <Link
              role="link"
              aria-label={t("ConsultationOrderSummary.panel.ariaLabelText")}
              underline="none"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => navigate("/home/more/get-support")}
            >
              {t("ConsultationOrderSummary.panel.contactLinkText")}
            </Link>
          </Typography>
          {!consultationPaymentReceipt?.hasDoneServiceRating && (
            <>
              <Typography variant="body2">
                {t("ConsultationOrderSummary.panel.secondDescription")}
              </Typography>
              <ButtonWithAnalytics
                page="ConsultationOrderSummary"
                text={t("ConsultationOrderSummary.panel.feedbackButtonText")}
                intent="navigational"
                onClick={onRateServiceClick}
                size="small"
                variant="outlined"
              >
                {t("ConsultationOrderSummary.panel.feedbackButtonText")}
              </ButtonWithAnalytics>
            </>
          )}
        </Stack>
        <Stack
          spacing={2}
          padding={2}
          pt={5}
          width="100%"
          sx={{ ...panelStyle, marginTop: 2 }}
        >
          <Typography variant="h4" fontWeight={600}>
            {t("OrderSummary.PanelTwo.title")}
          </Typography>
          <Divider />
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Stack>
              <Typography variant="h4" fontWeight={400}>
                {consultationType}
              </Typography>
              {consultationPaymentReceipt?.isDiscountApplied &&
                !isCareCardVoucherPurchase && (
                  <Typography
                    color="neutral.light"
                    variant="body2"
                    sx={{ textDecoration: "line-through" }}
                  >
                    {formatAmount(
                      consultationPaymentReceipt?.originalAmount,
                      consultationPaymentReceipt?.currencyCode
                    )}
                  </Typography>
                )}
            </Stack>
            <Typography variant="h4" color="primary">
              {getOrderSummaryTotalAmount()}
            </Typography>
          </Box>
          {consultationPaymentReceipt?.discountVoucherDetails?.discountAmount &&
            isDiscountVouchersEnabled && (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Typography variant="h4" fontWeight={400}>
                  {t("OrderSummary.discountVoucherApplied.text")}
                </Typography>
                <Typography variant="h4" color="primary">
                  {`-${formatAmount(
                    consultationPaymentReceipt?.discountVoucherDetails
                      .discountAmount,
                    consultationPaymentReceipt?.discountVoucherDetails
                      .currencyCode ?? "ZAR"
                  )}`}
                </Typography>
              </Box>
            )}
          <Divider sx={{ width: "100%" }} />
          <Stack direction={"row"} justifyContent="space-between">
            <Typography variant="h4">
              {t("OrderSummary.PanelTwo.totalText")}
            </Typography>
            {isCareCardVoucherPurchase ? (
              <Typography variant="h4" color="primary">
                {t("ConsultationOrderSummary.careCard.title")}
              </Typography>
            ) : (
              <Typography
                variant="h4"
                sx={{ fontSize: convertPxToRem(26) }}
                color="primary"
              >
                {formatAmount(
                  consultationPaymentReceipt?.paymentAmount ?? 0,
                  consultationPaymentReceipt?.currencyCode ?? "ZAR"
                )}
              </Typography>
            )}
          </Stack>

          {!isCareCardVoucherPurchase && (
            <ButtonWithAnalytics
              page="ConsultationOrderSummary"
              text={t("OrderSummary.bottomButton")}
              intent="navigational"
              onClick={onViewReceiptClick}
              size="small"
              variant="contained"
            >
              {t("OrderSummary.bottomButton")}
            </ButtonWithAnalytics>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
